/**
 * O bloco "Funcionários" da coluna ao lado da grade.
 *
 * Papel: lista quem está na escala da casa, cada pessoa com quantas folgas tem
 * no mês mostrado, contadas por `folgasNoMes`, e com o "×" que a tira da
 * escala; abaixo, o campo "Adicionar nome..." com o "+".
 *
 * Como usar: sem `podeEditar` — o nível `ver` — a lista é só de leitura, sem
 * "×" nem campo. O "×" não tira ninguém sozinho: chama `onRemover`, e a
 * confirmação é de quem monta o bloco.
 *
 * O nome digitado precisa ser de alguém do cadastro de pessoal da casa, entre
 * `candidatos`: a escala se liga por chave, e quem não está no cadastro precisa
 * ser cadastrado no RH antes de ganhar plantão ou folga. Quem o nome digitado
 * escolhe é decisão de `quemEntraNaEscala`, e o campo sugere os candidatos
 * enquanto se digita.
 */
import { type DiaCalendario, type MembroDaEquipe } from '@concierge/shared';

import { ErroDaApi } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import { folgasNoMes, nomeNaEscala, quemEntraNaEscala } from '../escala';
import { useIncluirNaEquipe } from '../hooks/usar-calendario';
import { type PessoaDaEscala } from '../types';
import {
  BOTAO_DE_LINHA,
  LinhaDeAcrescentar,
  LinhaDoPainel,
  ListaDoPainel,
  SecaoDoPainel,
} from './partes-do-painel';

interface ListaDeFuncionariosProps {
  /** Quem está na escala da casa, na ordem em que entrou nela. */
  equipe: MembroDaEquipe[];
  /** Os dias com registro do mês mostrado, de onde sai a contagem de folgas. */
  dias: DiaCalendario[];
  /** A casa do calendário aberto, que recebe quem for acrescentado. */
  residenciaId: string;
  /** Quem do cadastro de pessoal da casa pode entrar na escala. */
  candidatos: PessoaDaEscala[];
  /** Se aparecem o "×" e o campo de acrescentar. */
  podeEditar: boolean;
  /** Chamado pelo "×" de uma pessoa. */
  onRemover: (membro: MembroDaEquipe) => void;
}

/**
 * Escreve a contagem de folgas como ela se lê.
 *
 * @param quantidade - Quantos dias de folga a pessoa tem no mês.
 * @returns `1 folga` no singular, e `N folgas` nos demais, inclusive zero.
 */
function comoSeContam(quantidade: number): string {
  return quantidade === 1 ? '1 folga' : `${String(quantidade)} folgas`;
}

/**
 * O bloco "Funcionários".
 *
 * @param equipe - Quem está na escala; cada pessoa aparece pelo apelido, ou
 *   pelo nome completo de quem não tem apelido.
 * @param dias - Os dias com registro do mês mostrado, e só do mês: com os dias
 *   vizinhos da grade, a contagem passaria do mês.
 * @param residenciaId - A casa do calendário aberto.
 * @param candidatos - Quem pode ser acrescentado, oferecido pelo campo.
 * @param podeEditar - Se aparecem o "×" e o campo.
 * @param onRemover - Chamado pelo "×".
 * @returns A seção com uma faixa por pessoa e o campo de acrescentar.
 */
export function ListaDeFuncionarios({
  equipe,
  dias,
  residenciaId,
  candidatos,
  podeEditar,
  onRemover,
}: ListaDeFuncionariosProps) {
  const incluir = useIncluirNaEquipe();

  /** Põe na escala a pessoa digitada, ou diz por que não pôs. */
  async function handleAcrescentar(digitado: string): Promise<string | null> {
    const escolha = quemEntraNaEscala(
      digitado,
      equipe.map((membro) => membro.colaborador),
      candidatos,
    );
    if ('recusa' in escolha) return escolha.recusa;
    try {
      await incluir.mutateAsync({ residenciaId, colaboradorId: escolha.pessoa.id });
      return null;
    } catch (erro) {
      return erro instanceof ErroDaApi
        ? erro.message
        : 'A inclusão falhou antes de chegar ao servidor.';
    }
  }

  return (
    <SecaoDoPainel titulo="Funcionários">
      <ListaDoPainel vazia={equipe.length === 0} mensagemVazia="Nenhum funcionário cadastrado">
        {equipe.map((membro) => {
          const nome = nomeNaEscala(membro.colaborador);
          return (
            <LinhaDoPainel key={membro.id}>
              <span className="flex-1 font-medium">{nome}</span>
              <span className="rounded-[2px] border border-linha-forte bg-superficie px-[7px] py-px font-mono text-[10.5px] whitespace-nowrap text-texto-fraco">
                {comoSeContam(folgasNoMes(membro.colaborador.id, dias))}
              </span>
              {podeEditar && (
                <Button
                  type="button"
                  size="xs"
                  variant="destructive"
                  aria-label={`Remover ${nome} da lista`}
                  className={BOTAO_DE_LINHA}
                  onClick={() => onRemover(membro)}
                >
                  ×
                </Button>
              )}
            </LinhaDoPainel>
          );
        })}
      </ListaDoPainel>

      {podeEditar && (
        <LinhaDeAcrescentar
          placeholder="Adicionar nome..."
          sugestoes={candidatos.map(nomeNaEscala)}
          onAcrescentar={handleAcrescentar}
        />
      )}
    </SecaoDoPainel>
  );
}
